import { h, setChildren } from "../core/dom.js";
import { api } from "../api.js";
import { activeSession, refreshActiveSession } from "../store.js";
import { navigate } from "../core/router.js";
import { icon } from "../lib/icons.js";
import { topbar } from "../components/common.js";
import { spinner, errorState, toast } from "../components/ui.js";
import { MuscleMap } from "../components/MuscleMap.js";
import { relativeDay, fmtDurationLong } from "../lib/format.js";
import { formatProgramDays } from "./Workouts.js";

export function getWorkoutProgramInfo(w, program) {
  if (!program || !program.days || !program.days.length) {
    if (w?.active_program && w.active_program.days?.length) {
      const dayLabel = formatProgramDays(w.active_program.days);
      return {
        programId: w.active_program.id,
        programName: w.active_program.name,
        days: w.active_program.days,
        dayLabel,
        slotDetails: dayLabel,
        isToday: false,
        isDoneToday: false,
      };
    }
    return null;
  }

  const matchingDays = program.days.filter((d) => d.workout_id === w.id || d.workout?.id === w.id);
  if (!matchingDays.length) return null;

  const dayNumbers = matchingDays.map((d) => d.position + 1);
  const dayLabel = formatProgramDays(dayNumbers);

  const isTodayMatch = Boolean(
    program.today && (program.today.workout_id === w.id || program.today.workout?.id === w.id)
  );
  const isDoneToday = Boolean(isTodayMatch && program.anchored && program.done_today);
  const isToday = Boolean(isTodayMatch && !isDoneToday);

  const slotLabels = matchingDays.map((d) => {
    const num = `Day ${d.position + 1}`;
    return d.label && d.label !== w.name ? `${num} (${d.label})` : num;
  });

  return {
    programId: program.id,
    programName: program.name,
    days: dayNumbers,
    dayLabel,
    slotDetails: slotLabels.join(" · "),
    isToday,
    isDoneToday,
    matchingDays,
  };
}

export function sortWorkoutsForStart(list, program) {
  return [...list].sort((a, b) => {
    const infoA = getWorkoutProgramInfo(a, program);
    const infoB = getWorkoutProgramInfo(b, program);
    const rankA = infoA ? (infoA.isToday ? -2 : -1) : 0;
    const rankB = infoB ? (infoB.isToday ? -2 : -1) : 0;
    if (rankA !== rankB) return rankA - rankB;
    if (infoA && infoB) {
      const minDayA = Math.min(...infoA.days);
      const minDayB = Math.min(...infoB.days);
      if (minDayA !== minDayB) return minDayA - minDayB;
    }
    return 0;
  });
}

export default function StartScreen() {
  const root = h("div", { class: "screen start-screen" });
  let starting = false;
  load();

  async function load() {
    setChildren(root, topbar("Start a workout", { eyebrow: "TRAINING", icon: "dumbbell" }), spinner());
    try {
      const [workouts, lastList, program] = await Promise.all([
        api.workouts(),
        api.sessionHistory("?limit=1").catch(() => []),
        api.programToday().catch(() => null),
      ]);
      render(workouts, lastList[0] || null, program);
    } catch (e) {
      setChildren(root, topbar("Start a workout", { eyebrow: "TRAINING", icon: "dumbbell" }), errorState(e.message, load));
    }
  }

  async function begin(opts) {
    if (activeSession()) return navigate("/session");
    if (starting) return;
    starting = true;
    root.setAttribute("aria-busy", "true");
    root.querySelectorAll(".start-option, .template-start").forEach(button => { button.disabled = true; });
    try {
      const session = await api.startSession(opts || {});
      activeSession(session);
      navigate("/session");
    } catch (e) {
      if (e.status === 409) {
        await refreshActiveSession();
        navigate("/session");
      } else {
        toast(e.message, "error");
      }
    } finally {
      starting = false;
      root.removeAttribute("aria-busy");
      root.querySelectorAll(".start-option, .template-start").forEach(button => { button.disabled = false; });
    }
  }

  function render(workouts, last, program) {
    // Today's program day leads here too (same rule as the dashboard CTA), so entering via
    // the generic "Start a workout" path never silently loses program tracking.
    const todayDay = program && program.today && program.today.workout && !(program.anchored && program.done_today) ? program.today : null;
    const subText = todayDay
      ? `Up next: ${todayDay.label || todayDay.workout.name}`
      : (program ? `Active program: ${program.name}` : "Your plan. Your pace.");

    setChildren(root,
      topbar("Start a workout", {
        eyebrow: program ? `ACTIVE: ${program.name.toUpperCase()}` : "TRAINING",
        sub: subText,
        icon: "dumbbell",
        badge: program ? (program.anchored ? "Calendar" : "Program") : null,
      }),
      activeSession() &&
        h("button", { class: "btn btn-success btn-block btn-lg", onClick: () => navigate("/session") }, icon("play", { size: 22, fill: true }), "Resume current workout"),

      !activeSession() && h("div", { class: "start-options" },
        todayDay ? startOption("calendar", "TODAY’S PROGRAM", todayDay.label || todayDay.workout.name, program.name,
          () => begin({ workout_id: todayDay.workout.id, program_id: program.id }), true) : null,
        last ? startOption("history", "PICK UP WHERE YOU LEFT OFF", `Repeat: ${last.name || "last workout"}`,
          [relativeDay(last.started_at), last.duration_seconds ? fmtDurationLong(last.duration_seconds) : null, "Sets prefilled"].filter(Boolean).join(" · "),
          () => begin({ repeat_last: true }), !todayDay) : null,
        startOption("plus", "MAKE IT YOURS", "Freestyle workout", "Add exercises as you go", () => begin(null), !todayDay && !last)),

      h("div", { class: "section-title row-between" }, "Your workouts", h("span", { class: "dim" }, `${workouts.length} templates`)),
      workouts.length
        ? templateBrowser(workouts, program)
        : h(
            "div",
            { class: "card center" },
            h("p", { class: "dim" }, "No templates yet."),
            h("button", { class: "btn btn-primary mt", onClick: () => navigate("/workouts/new") }, "Create a workout")
          )
    );
  }

  function startOption(ic, label, title, detail, onClick, primary = false) {
    return h("button", { class: ["start-option", primary && "featured"], onClick },
      h("span", { class: "start-option-icon" }, icon(ic, { size: 23 })),
      h("span", { class: "start-option-copy" }, h("span", { class: "eyebrow" }, label), h("strong", null, title), h("span", { class: "start-option-detail" }, detail)),
      icon("forward", { size: 20 }));
  }

  function templateBrowser(workouts, program) {
    const sorted = sortWorkoutsForStart(workouts, program);
    const cards = sorted.map(w => {
      const info = getWorkoutProgramInfo(w, program);
      return { workout: w, info, node: templateCard(w, info) };
    });
    const message = h("p", { class: "dim center", hidden: true }, "No matching workouts. Try another name or exercise.");
    const search = h("input", { class: "search-input", type: "search", placeholder: "Find a workout, program, or exercise…", "aria-label": "Find a workout", oninput: e => {
      const query = e.target.value.trim().toLocaleLowerCase();
      let count = 0;
      for (const { workout, info, node } of cards) {
        const textToSearch = [
          workout.name,
          info?.programName || "",
          info?.dayLabel || "",
          info?.slotDetails || "",
          ...workout.exercises.map(ex => ex.exercise_name)
        ].join(" ").toLocaleLowerCase();
        const match = textToSearch.includes(query);
        node.hidden = !match;
        if (match) count++;
      }
      message.hidden = count !== 0;
    } });
    return h("div", null, h("div", { class: "search-wrap" }, icon("search", { size: 18 }), search),
      h("div", { class: "start-template-grid" }, ...cards.map(c => c.node)), message);
  }

  function templateCard(w, programInfo) {
    const primary = w.exercises.flatMap((e) => e.primary_muscle_ids || []);
    const mini = MuscleMap({ primary });
    mini.classList.add("mini");
    mini.style.maxWidth = "104px";

    const isToday = programInfo?.isToday;
    const inProgram = Boolean(programInfo);

    const badge = inProgram
      ? h("div", { class: "template-program-badge-wrap" },
          h("span", {
            class: ["badge", "badge-program", isToday && "badge-today"],
            title: `In active program ${programInfo.programName}: ${programInfo.slotDetails || programInfo.dayLabel}`
          },
            icon("calendar", { size: 12 }),
            h("span", { class: "program-name" }, isToday ? "TODAY" : programInfo.programName),
            h("span", { class: "dot-sep", "aria-hidden": "true" }, "·"),
            h("span", { class: "program-days" }, programInfo.slotDetails || programInfo.dayLabel)
          )
        )
      : null;

    return h(
      "button",
      {
        class: ["card-tap", "template-start", inProgram && "in-program", isToday && "is-today"],
        onClick: () => begin({ workout_id: w.id, program_id: programInfo?.programId })
      },
      mini,
      h(
        "div",
        { class: "grow" },
        badge,
        h("div", { style: { fontWeight: 700, fontSize: "1.05rem" } }, w.name),
        h("div", { class: "dim" }, `${w.exercises.length} exercise${w.exercises.length === 1 ? "" : "s"} · ${w.exercises.reduce((n, ex) => n + (ex.target_sets || 3), 0)} sets`),
        h("div", { class: "template-exercises" }, w.exercises.slice(0, 3).map(ex => ex.exercise_name).join(" · "))
      ),
      h("span", { class: "icon-btn" }, icon("play", { size: 20, fill: true }))
    );
  }

  return root;
}
